import React, { useCallback, useRef } from 'react';
import { AuthorityChip } from '../Authority';
import { ICON_PATH, NODE_SWATCH } from '../DocSection';
import type { PlanNode } from '../types';
import { useCanvas, useNodeBox } from './canvasStore';
import type { Side } from './geometry';
import { useRuntime } from './runtime';
import { useCardDrag } from './useCardDrag';

interface CanvasNodeProps {
  node: PlanNode;
  /** May move this node: a manager, or its authority, on a writable plan. */
  canEdit: boolean;
  /** Which handles start an arrow: both for a manager, the outgoing one for an authority. */
  arrows: 'both' | 'out' | 'none';
  /** How much changed here since this person last looked. */
  unread: number;
}

const CanvasNode: React.FC<CanvasNodeProps> = ({ node, canEdit, arrows, unread }) => {
  const { id } = node;
  const rt = useRuntime();
  // Trap 7: this node subscribes to its own box and its own selected flag, nothing else.
  const box = useNodeBox(id);
  const selected = useCanvas((s) => s.selection.has(id));
  const el = useRef<HTMLDivElement | null>(null);
  const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } = useCardDrag(id, canEdit, el);
  const proposed = node.status === 'proposed';
  const done = !!node.done_at;

  const setRef = useCallback(
    (div: HTMLDivElement | null) => {
      el.current = div;
      rt.observe(id, div);
    },
    [rt, id],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      rt.openPanel(id, true);
    } else if (e.key === ' ') {
      e.preventDefault();
      rt.selection.clickNode(id, e);
    }
  };

  if (!box) return null;

  const handle = (side: Side) => {
    const live = arrows === 'both' || (arrows === 'out' && side === 'right');
    return (
      <span
        aria-hidden="true"
        data-handle-side={side}
        className={`np-handle np-handle-${side} ${live ? 'np-handle-live' : ''}`}
        onPointerDown={live ? (e) => rt.connect.onHandlePointerDown(e, id, side) : undefined}
      />
    );
  };

  return (
    <div
      ref={setRef}
      data-node-id={id}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      aria-label={`${node.title}${proposed ? ', proposed' : ''}${done ? ', done' : ''}${unread > 0 ? `, ${unread} new` : ''}`}
      className={`np-node ${proposed ? 'np-node-proposed' : ''} ${done ? 'np-node-done' : ''} ${canEdit ? 'np-node-draggable' : ''}`}
      // Trap 3, edges to unmeasured nodes: hidden until the ResizeObserver reports a size.
      style={{
        transform: `translate(${box.x}px, ${box.y}px)`,
        visibility: box.measured ? 'visible' : 'hidden',
        ['--np-card-tint' as any]: node.color === 'none' ? undefined : NODE_SWATCH[node.color],
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onClick={(e) => {
        // A plain click or tap opens the panel; a multi-select click only toggles.
        if (e.ctrlKey || e.metaKey) rt.selection.clickNode(id, e);
        else rt.openPanel(id, false);
      }}
      onKeyDown={onKeyDown}
    >
      <div className="np-node-title">{node.title}</div>
      {(proposed || done || node.authority_name || unread > 0) && (
        <div className="np-node-meta">
          {proposed && <span className="np-pill">Proposed{node.proposed_by_name ? ` by ${node.proposed_by_name}` : ''}</span>}
          {done && (
            <span className="np-pill np-pill-icon">
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none">
                <path fill="currentColor" d={ICON_PATH.check} />
              </svg>
              Done
            </span>
          )}
          <AuthorityChip name={node.authority_name} />
          {unread > 0 && (
            <span
              className="np-pill tabular-nums"
              // Inline: np-pill sets the quiet color, and a class here would lose to it on load order.
              style={{ color: 'var(--on-surface)', fontWeight: 600 }}
              title={`${unread} new since you last looked`}
            >
              {unread} new
            </span>
          )}
        </div>
      )}
      {!proposed && handle('left')}
      {!proposed && handle('right')}
    </div>
  );
};

export default React.memo(CanvasNode);
